import { ReactElement, ReactNode } from 'react'
import { GoogleTagManager } from '@next/third-parties/google'
import { NextPage } from 'next'
import { type AppContext, type AppProps, default as NextApp } from 'next/app'
import dynamic from 'next/dynamic'

import { PortalRoot } from '@/components/base/Portal'
import SVGSprites from '@/components/base/SVGSprites'
import { LenisProvider } from '@/contexts/lenis'
import ModalProvider from '@/contexts/modal/ModalProvider'
import { Store, StoreProvider } from '@/contexts/store'
import { ThemeProvider } from '@/contexts/theme'
import { mapOptionsDataProps } from '@/utils/pageData'
import { getPromiseFromFetch } from '@/utils/request'
import { API, GTM_ID } from '@/constants'

import '@/styles/globals.css'

import '@/base/gsap'

const GridHelper = dynamic(() => import('@/components/base/GridHelper'), {
  ssr: false
})

const CookieBanner = dynamic(() => import('@/components/base/CookieBanner'), {
  ssr: false
})

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  // eslint-disable-next-line no-unused-vars
  getLayout?: (page: ReactElement, props: AppProps) => ReactNode
}

export default function App({
  Component,
  pageProps,
  menu,
  strings
}: AppProps & Store & { Component: NextPageWithLayout }) {
  const getLayout = Component.getLayout ?? ((page) => page)

  return (
    <>
      <StoreProvider menu={menu} strings={strings}>
        <ThemeProvider theme={pageProps?.acf?.theme} fluid>
          <LenisProvider>
            <ModalProvider>
              {!process.env.production ? <GridHelper /> : null}
              {getLayout(<Component {...pageProps} />, pageProps)}
              <PortalRoot id="modal" />
            </ModalProvider>
          </LenisProvider>
          <SVGSprites />
        </ThemeProvider>
        <CookieBanner />
      </StoreProvider>
      <GoogleTagManager gtmId={GTM_ID} />
    </>
  )
}

App.getInitialProps = async (appContext: AppContext) => {
  const appProps = await NextApp.getInitialProps(appContext)

  const { data: menuData } = await getPromiseFromFetch({
    method: 'get',
    url: `${API.MENU_LOCATIONS}`
  })

  const { data: optionsData } = await getPromiseFromFetch({
    method: 'get',
    url: `${API.STRINGS}`
  })

  return {
    ...appProps,
    strings: await mapOptionsDataProps(optionsData),
    menu: Object.keys(menuData).length > 0 ? menuData : {}
  }
}
